import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type UniqueIdentifier
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  type SortingStrategy
} from '@dnd-kit/sortable';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Table } from 'v5/design-system/Table';
import { CollectionDropIndicatorFloatingLine } from 'v5/patterns/dnd/CollectionDropIndicator';
import { cn } from 'v5/platform/dom/cn';
import {
  EntityTableDragOverlay,
  EntityTableRowDndProvider,
  type EntityTableRowDndHandleProps
} from './EntityTableDragHandle';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

/** Keeps list layout fixed during drag — only overlay + drop line move. */
const fixedLayoutSortableStrategy: SortingStrategy = () => null;

const collisionDetection: CollisionDetection = args =>
  closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      container => container.id !== args.active.id
    )
  });

type DropLineRect = { top: number; left: number; width: number };

export type SortableEntityTableRowReorderConfig<TId extends string | number> = {
  enabled: boolean;
  /**
   * Rows that cannot be dragged (e.g. locked / pinned leading fields).
   * Leading disabled rows form a block that other rows cannot move above.
   * Default handle cell shows {@link EntityTableLockedHandle} for these ids.
   */
  isItemDisabled?: (id: TId) => boolean;
  onReorder: (orderedIds: TId[]) => void;
  /**
   * Label (or node) for the default drag overlay.
   * Prefer this over a custom {@link renderDragOverlay} unless you need extra chrome.
   */
  getItemLabel?: (id: TId) => ReactNode;
  /** Full overlay override; when omitted, uses {@link EntityTableDragOverlay} + getItemLabel. */
  renderDragOverlay?: (id: TId) => ReactNode;
  /**
   * Overrides the built-in drag column cell (default: lock icon when disabled,
   * otherwise {@link EntityTableDragHandle}).
   */
  renderHandleCell?: (id: TId) => ReactNode;
};

type RowReorderInternalContextValue = {
  enabled: boolean;
  isItemDisabled?: (id: string | number) => boolean;
};

const RowReorderInternalContext = createContext<RowReorderInternalContextValue>(
  { enabled: false }
);

/** Index of the first reorderable row (after a leading disabled / locked block). */
function getMinReorderIndex<TId extends string | number>(
  itemIds: readonly TId[],
  isItemDisabled?: (id: TId) => boolean
): number {
  let minIndex = 0;
  while (minIndex < itemIds.length && isItemDisabled?.(itemIds[minIndex]!)) {
    minIndex += 1;
  }
  return minIndex;
}

function getRowRectById(id: string | number): DOMRect | null {
  const el = document.querySelector(
    `[data-item-id="${CSS.escape(String(id))}"]`
  );
  return el?.getBoundingClientRect() ?? null;
}

function dropLineFromRect(
  rect: { top: number; left: number; width: number; height: number },
  edge: 'top' | 'bottom'
): DropLineRect {
  return {
    top: edge === 'top' ? rect.top : rect.top + rect.height,
    left: rect.left,
    width: rect.width
  };
}

function computeDropLineRect<TId extends string | number>({
  over,
  active,
  itemIds,
  isItemDisabled,
  delta
}: {
  over: DragMoveEvent['over'] | null;
  active: DragMoveEvent['active'];
  itemIds: readonly TId[];
  isItemDisabled?: (id: TId) => boolean;
  delta?: { x: number; y: number };
}): DropLineRect | null {
  if (!over || !Array.isArray(itemIds)) return null;

  const overIndex = itemIds.findIndex(id => String(id) === String(over.id));
  if (overIndex < 0) return null;

  const minIndex = getMinReorderIndex(itemIds, isItemDisabled);
  // Hovering the locked leading block: snap to the first legal slot so the
  // indicator matches handleDragEnd clamping (no line above pinned rows).
  if (overIndex < minIndex) {
    const legalId = itemIds[minIndex];
    if (legalId == null) return null;
    const legalRect = getRowRectById(legalId);
    if (!legalRect) return null;
    return dropLineFromRect(legalRect, 'top');
  }

  const overRect = over.rect;
  const initial = active.rect.current.initial;
  const translated = active.rect.current.translated;
  const dragTop = translated?.top ?? (initial?.top ?? 0) + (delta?.y ?? 0);
  const dragHeight = initial?.height ?? overRect.height;
  const dragCenterY = dragTop + dragHeight / 2;
  const overMidY = overRect.top + overRect.height / 2;
  const edge: 'top' | 'bottom' = dragCenterY < overMidY ? 'top' : 'bottom';
  return dropLineFromRect(overRect, edge);
}

export function SortableEntityTableRowReorderRoot<TId extends string | number>({
  itemIds,
  rowReorder,
  children
}: {
  itemIds: TId[];
  rowReorder: SortableEntityTableRowReorderConfig<TId> | undefined;
  children: ReactNode;
}) {
  const portalContainer = usePortalContainer();
  const [activeId, setActiveId] = useState<TId | null>(null);
  const [dropLineRect, setDropLineRect] = useState<DropLineRect | null>(null);

  // Keep latest ids/config in refs so drag handlers never see a stale non-array
  // (or HMR-mismatched positional args) mid-gesture.
  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;
  const isItemDisabledRef = useRef(rowReorder?.isItemDisabled);
  isItemDisabledRef.current = rowReorder?.isItemDisabled;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const clearDragUi = useCallback(() => {
    setActiveId(null);
    setDropLineRect(null);
  }, []);

  const syncDragUi = useCallback((event: DragMoveEvent) => {
    setDropLineRect(
      computeDropLineRect({
        over: event.over,
        active: event.active,
        itemIds: itemIdsRef.current,
        isItemDisabled: isItemDisabledRef.current,
        delta: event.delta
      })
    );
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as TId);
    setDropLineRect(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    clearDragUi();
    if (!rowReorder || !over || active.id === over.id) return;

    const ids = itemIdsRef.current;
    if (!Array.isArray(ids)) return;

    const oldIndex = ids.findIndex(id => String(id) === String(active.id));
    let newIndex = ids.findIndex(id => String(id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    // Pin below a leading disabled block (e.g. locked fields first).
    newIndex = Math.max(
      newIndex,
      getMinReorderIndex(ids, rowReorder.isItemDisabled)
    );
    if (oldIndex === newIndex) return;

    rowReorder.onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  const enabled = Boolean(rowReorder?.enabled);

  const contextValue = useMemo(
    () => ({
      enabled,
      isItemDisabled: rowReorder?.isItemDisabled as
        | ((id: string | number) => boolean)
        | undefined
    }),
    [enabled, rowReorder?.isItemDisabled]
  );

  if (!enabled || !rowReorder) {
    return (
      <RowReorderInternalContext.Provider value={{ enabled: false }}>
        {children}
      </RowReorderInternalContext.Provider>
    );
  }

  return (
    <RowReorderInternalContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        collisionDetection={collisionDetection}
        measuring={{
          droppable: { strategy: MeasuringStrategy.BeforeDragging }
        }}
        onDragStart={handleDragStart}
        onDragMove={syncDragUi}
        onDragOver={syncDragUi}
        onDragCancel={clearDragUi}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds as UniqueIdentifier[]}
          strategy={fixedLayoutSortableStrategy}
        >
          {children}
        </SortableContext>
        <CollectionDropIndicatorFloatingLine rect={dropLineRect} />
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {activeId != null
              ? (rowReorder.renderDragOverlay?.(activeId) ?? (
                  <EntityTableDragOverlay>
                    {rowReorder.getItemLabel?.(activeId)}
                  </EntityTableDragOverlay>
                ))
              : null}
          </DragOverlay>,
          portalContainer
        )}
      </DndContext>
    </RowReorderInternalContext.Provider>
  );
}

type SortableRowProps = Omit<
  React.ComponentPropsWithoutRef<typeof Table.Row>,
  'id'
> & {
  id: string | number;
};

/**
 * Drop-in for {@link Table.Row} when row reorder is active.
 * Must be rendered inside {@link SortableEntityTableRowReorderRoot} with enabled reorder.
 */
export const SortableEntityTableSortableRow = forwardRef<
  HTMLTableRowElement,
  SortableRowProps
>(function SortableEntityTableSortableRow(
  { id, className, children, ...rowProps },
  forwardedRef
) {
  const { enabled, isItemDisabled } = useContext(RowReorderInternalContext);
  const disabled = !enabled || Boolean(isItemDisabled?.(id));

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useSortable({
      id,
      disabled,
      animateLayoutChanges: () => false,
      transition: null
    });

  const handleProps: EntityTableRowDndHandleProps = {
    attributes: attributes as EntityTableRowDndHandleProps['attributes'],
    listeners: listeners as EntityTableRowDndHandleProps['listeners'],
    setActivatorNodeRef,
    disabled,
    isDragging
  };

  return (
    <EntityTableRowDndProvider value={handleProps}>
      <Table.Row
        ref={node => {
          setNodeRef(node);
          if (typeof forwardedRef === 'function') {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
        }}
        className={cn(className, isDragging && 'opacity-40')}
        data-item-id={String(id)}
        {...rowProps}
      >
        {children}
      </Table.Row>
    </EntityTableRowDndProvider>
  );
});
